import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import type { WidgetConfig, WidgetAuthState } from './types';

interface WidgetAuthContextValue extends WidgetAuthState {
    refreshToken: () => Promise<string | undefined>;
    login: () => void;
}

const WidgetAuthContext = createContext<WidgetAuthContextValue | null>(null);

export function useWidgetAuth() {
    const context = useContext(WidgetAuthContext);
    if (!context) {
        throw new Error('useWidgetAuth must be used within a WidgetAuthProvider');
    }
    return context;
}

interface WidgetAuthProviderProps {
    config: WidgetConfig;
    children: React.ReactNode;
}

/**
 * Derives the origin of the Atlas frontend from the atlas-widget script tag.
 * Falls back to window.location.origin if the script tag cannot be found.
 */
function getWidgetOrigin(): string {
    const scripts = document.getElementsByTagName('script');
    for (let i = 0; i < scripts.length; i++) {
        const src = scripts[i].src;
        if (src && src.includes('atlas-widget')) {
            return new URL(src).origin;
        }
    }
    return window.location.origin;
}

function buildBridgeCallbackUrl(widgetOrigin: string, callbackUrl: string): string | null {
    try {
        const url = new URL(callbackUrl);
        if (url.origin !== widgetOrigin) {
            return null;
        }

        const bridgeUrl = new URL(`${widgetOrigin}/widget-auth.html`);
        for (const [key, value] of url.searchParams) {
            bridgeUrl.searchParams.append(key, value);
        }

        // Keycloak defaults to response_mode=fragment. If the bridge iframe is
        // already at /widget-auth.html, assigning only a new hash does not
        // reliably reload the iframe, so the auth bridge script never re-runs.
        bridgeUrl.searchParams.set('atlasWidgetCallback', Date.now().toString());
        bridgeUrl.hash = url.hash;

        return bridgeUrl.toString();
    } catch {
        return null;
    }
}

const BRIDGE_AUTH_TIMEOUT_MS = 15000;

/**
 * Authentication provider for the widget.
 *
 * Instead of running keycloak-js directly on the host page, we load a hidden
 * iframe from the Atlas frontend origin (`/widget-auth.html`). All Keycloak
 * interactions (silent SSO check, token exchange) happen from that origin, so
 * only the Atlas domain needs to be registered in Keycloak — no host-app URLs
 * need to be added to Valid Redirect URIs or Web Origins.
 *
 * Communication with the bridge iframe uses postMessage.
 */
export const WidgetAuthProvider: React.FC<WidgetAuthProviderProps> = ({
    config: _config,
    children,
}) => {
    const [authState, setAuthState] = useState<WidgetAuthState>({
        isAuthenticated: false,
        isLoading: true,
        token: null,
        error: null,
    });

    const iframeRef = useRef<HTMLIFrameElement | null>(null);
    const widgetOriginRef = useRef<string>(getWidgetOrigin());
    const tokenRef = useRef<string | null>(null);
    const popupCallbackReceivedRef = useRef(false);
    const bridgeAuthTimeoutRef = useRef<number | null>(null);

    const clearBridgeAuthTimeout = useCallback(() => {
        if (bridgeAuthTimeoutRef.current) {
            window.clearTimeout(bridgeAuthTimeoutRef.current);
            bridgeAuthTimeoutRef.current = null;
        }
    }, []);

    const startBridgeAuthTimeout = useCallback((stage: string) => {
        clearBridgeAuthTimeout();
        bridgeAuthTimeoutRef.current = window.setTimeout(() => {
            bridgeAuthTimeoutRef.current = null;
            tokenRef.current = null;
            console.error(`[Atlas Widget Auth] Timed out waiting for auth bridge result after ${stage}.`);
            setAuthState({
                isAuthenticated: false,
                isLoading: false,
                token: null,
                error: 'login_required',
            });
        }, BRIDGE_AUTH_TIMEOUT_MS);
    }, [clearBridgeAuthTimeout]);

    const createBridgeIframe = useCallback(() => {
        const widgetOrigin = widgetOriginRef.current;

        // Remove any existing bridge iframe
        if (iframeRef.current) {
            document.body.removeChild(iframeRef.current);
            iframeRef.current = null;
        }

        const iframe = document.createElement('iframe');
        iframe.src = `${widgetOrigin}/widget-auth.html`;
        iframe.style.cssText = 'display:none;width:0;height:0;border:none;position:absolute;';
        iframe.setAttribute('aria-hidden', 'true');
        document.body.appendChild(iframe);
        iframeRef.current = iframe;
    }, []);

    useEffect(() => {
        const widgetOrigin = widgetOriginRef.current;

        const messageHandler = (event: MessageEvent) => {
            // Only accept messages from the Atlas frontend origin
            if (event.origin !== widgetOrigin) return;
            if (!event.data || typeof event.data !== 'object') return;

            const { type } = event.data;

            if (type === 'atlas-widget-auth-result') {
                clearBridgeAuthTimeout();
                const { authenticated, token, error } = event.data;
                if (!authenticated && error) {
                    console.warn('[Atlas Widget Auth] Auth bridge reported unauthenticated:', error);
                }
                tokenRef.current = token || null;
                setAuthState({
                    isAuthenticated: authenticated,
                    isLoading: false,
                    token: token || null,
                    error: authenticated ? null : 'login_required',
                });
            } else if (type === 'atlas-widget-auth-token-refreshed') {
                clearBridgeAuthTimeout();
                const { token } = event.data;
                if (token) {
                    tokenRef.current = token;
                    setAuthState(prev => ({ ...prev, token }));
                }
            } else if (type === 'atlas-widget-auth-session-expired') {
                clearBridgeAuthTimeout();
                tokenRef.current = null;
                setAuthState({
                    isAuthenticated: false,
                    isLoading: false,
                    token: null,
                    error: 'login_required',
                });
            } else if (type === 'atlas-widget-auth-callback') {
                if (popupCallbackReceivedRef.current) return;

                const callbackUrl = typeof event.data.url === 'string' ? event.data.url : null;
                if (!callbackUrl) return;

                const bridgeCallbackUrl = buildBridgeCallbackUrl(widgetOrigin, callbackUrl);
                if (!bridgeCallbackUrl || !iframeRef.current) return;

                popupCallbackReceivedRef.current = true;
                setAuthState(prev => ({ ...prev, isLoading: true }));
                startBridgeAuthTimeout('popup callback');
                iframeRef.current.src = bridgeCallbackUrl;
            }
        };

        window.addEventListener('message', messageHandler);
        createBridgeIframe();

        return () => {
            window.removeEventListener('message', messageHandler);
            clearBridgeAuthTimeout();
            if (iframeRef.current) {
                document.body.removeChild(iframeRef.current);
                iframeRef.current = null;
            }
        };
    }, [clearBridgeAuthTimeout, createBridgeIframe, startBridgeAuthTimeout]);

    const refreshToken = useCallback((): Promise<string | undefined> => {
        const iframe = iframeRef.current;
        const widgetOrigin = widgetOriginRef.current;

        if (!iframe?.contentWindow) {
            return Promise.resolve(undefined);
        }

        return new Promise((resolve) => {
            const handler = (event: MessageEvent) => {
                if (event.origin !== widgetOrigin) return;
                if (event.data?.type !== 'atlas-widget-auth-token-refreshed') return;
                window.removeEventListener('message', handler);
                clearTimeout(timeout);
                resolve(event.data.token || undefined);
            };

            const timeout = setTimeout(() => {
                window.removeEventListener('message', handler);
                resolve(tokenRef.current || undefined);
            }, 5000);

            window.addEventListener('message', handler);
            iframe.contentWindow!.postMessage(
                { type: 'atlas-widget-auth-refresh-token' },
                widgetOrigin
            );
        });
    }, []);

    const login = useCallback(() => {
        const iframe = iframeRef.current;
        const widgetOrigin = widgetOriginRef.current;

        if (!iframe?.contentWindow) return;

        popupCallbackReceivedRef.current = false;

        // We ask the bridge to create a login URL pointing back to our
        // login-callback.html at Atlas origin (so no host URL is needed in Keycloak)
        const callbackUri = `${widgetOrigin}/login-callback.html`;

        const handler = (event: MessageEvent) => {
            if (event.origin !== widgetOrigin) return;
            if (event.data?.type !== 'atlas-widget-auth-login-url') return;
            window.removeEventListener('message', handler);
            clearTimeout(timeout);

            const popup = window.open(event.data.url, '_blank', 'width=600,height=700');
            if (!popup) {
                // Popup blocked — fall back to full-page redirect
                window.location.href = event.data.url;
                return;
            }

            // The callback page posts the OAuth callback URL before closing. If
            // that message is blocked or missed, fall back to the older SSO check.
            const pollTimer = setInterval(() => {
                if (popup.closed) {
                    clearInterval(pollTimer);
                    if (!popupCallbackReceivedRef.current) {
                        setAuthState(prev => ({ ...prev, isLoading: true }));
                        startBridgeAuthTimeout('popup close recheck');
                        iframeRef.current?.contentWindow?.postMessage(
                            { type: 'atlas-widget-auth-recheck' },
                            widgetOrigin
                        );
                    }
                }
            }, 500);
        };

        const timeout = setTimeout(() => window.removeEventListener('message', handler), 5000);
        window.addEventListener('message', handler);
        iframe.contentWindow.postMessage(
            { type: 'atlas-widget-auth-get-login-url', redirectUri: callbackUri },
            widgetOrigin
        );
    }, [startBridgeAuthTimeout]);

    const contextValue: WidgetAuthContextValue = {
        ...authState,
        refreshToken,
        login,
    };

    return (
        <WidgetAuthContext.Provider value={contextValue}>
            {children}
        </WidgetAuthContext.Provider>
    );
};
